import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {GenericDatagridService} from '../../core/services/generics/generic-datagrid.service';
import {TaxesService} from '../../core/services/entities/taxes.service';
import {HELP_FOLDERS, MSG_KEY, MSG_SEVERITY} from "../../core/constants";
import {TaxeDTO} from "../../core/dtos/taxe-dto";
import {Subscription} from "rxjs";
import {GraphQLService} from "../../core/services/technique/graphql.service";
import {UtilsService} from "../../core/utils/utils.service";
import {saveAs} from "file-saver";
import {InternationalizationService} from "../../core/services/i8n/i8n.service";
import {ToastService} from "../../core/services/technique/toast.service";
import {confirm} from "devextreme/ui/dialog";
import {ResponseWrapper} from "../../core/suppliers/wrappers/response-wrapper";
import {IsDeletableObject} from "../../core/models/is-deletable-object";
import {DxTooltipComponent} from "devextreme-angular";

@Component({
  selector: 'yo-taxes',
  templateUrl: './taxes.component.html',
  styleUrls: ['./taxes.component.scss']
})
export class TaxesComponent implements OnInit, OnDestroy {

  pathFile: string = HELP_FOLDERS.REFERENTIEL + '/taxes/taxes';

  taxes: TaxeDTO[] = [];

  selectedRows: number[] = [];

  subGraphQL: Subscription;

  subXls: Subscription;

  subImport: Subscription;

  subTaxSaved: Subscription;

  errors: any[] = [];

  displayDeletePopupErrors: boolean = false;

  displayImportPopupErrors: boolean = false;

  @ViewChild("importTooltip") importTooltip: DxTooltipComponent;

  constructor(public taxesSvc: TaxesService,
              public gds: GenericDatagridService,
              private readonly graphqlSvc: GraphQLService,
              private utilsSvc: UtilsService,
              private readonly i8nSvc: InternationalizationService,
              private readonly toastSvc: ToastService) {
  }

  ngOnInit() {
    this.fetchData();
    this.subscribeTaxSaved();
  }

  ngOnDestroy() {
    this.utilsSvc.unsubscribe(this.subGraphQL);
    this.utilsSvc.unsubscribe(this.subXls);
    this.utilsSvc.unsubscribe(this.subImport);
    this.utilsSvc.unsubscribe(this.subTaxSaved);
  }

  fetchData = (): void => {
    this.subGraphQL = this.graphqlSvc.sendQuery(`
      {
          allTaxes {
              id,
              libelle,
              site { id, libelle },
              code,
              valeur,
              actif
          }
      }
    `)
      .subscribe((res) => {
        this.taxes = res.allTaxes;
      });
  }

  openDialogEdition = (taxe?: TaxeDTO): void => {
    this.taxesSvc.announceOpeningEditionDialog(taxe);
  }

  exportXslx = (): void => {
    this.subXls = this.taxesSvc.export(this.selectedRows)
      .subscribe(response => {
        const filename = `taxes-export-${new Date().getTime()}.xlsx`;
        const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
        this.selectedRows = [];
        saveAs(blob, filename);
      });
  }

  canDelete = (): boolean => this.selectedRows?.length > 0;

  deleteValues = async (): Promise<void> => {
    this.errors = [];
    let idsToDeleteInTable = Object.assign([], this.selectedRows);

    let isDeletionValidated: boolean = await confirm(this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? "CONFIRM_DELETION_PLURAL" : "CONFIRM_DELETION_SINGULAR", null), this.i8nSvc.getLabelFromCode("Suppression", null));

    if (!isDeletionValidated)
      return new Promise(null);

    this.taxesSvc.delete(this.selectedRows).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;

      if (res) {
        if (res?.messagesErrorList?.length) {
          for (const error of res.messagesErrorList) {
            const labelsError: any = this.i8nSvc.getLabelFromCode(error.code, null);
            const infosLine: string = labelsError.replace('{}', error.args);
            this.errors.push({infosLine});
            const elementToKeep = this.taxes.find(u => u.code === error.args);
            idsToDeleteInTable = idsToDeleteInTable.filter(id => elementToKeep.id !== id);
          }
          this.displayDeleteErrors();
        } else {
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, idsToDeleteInTable.length > 1 ? `Les taxes ont bien été supprimées` : `La taxe a bien été supprimée`);
        }
        this.taxes = this.taxes.filter(taxe => !idsToDeleteInTable.find(id => id === taxe.id));
      }
    });
  }

  displayDeleteErrors = (): void => {
    this.displayDeletePopupErrors = true;
  }

  closeErrors = (): void => {
    this.errors = [];
    this.displayDeletePopupErrors = false;
    this.displayImportPopupErrors = false;
  }

  displayImportErrors = (): void => { this.displayImportPopupErrors = true; }

  importXslx = ($event: any): void => {
    this.errors = [];
    if ($event.target.files && $event.target.files.length) {
      const file: File = $event.target.files[0];
      this.subImport = this.taxesSvc.importFromXlsx(file)
        .subscribe((response) => {
          const res: any = response.one;
          if (res.allElementsImported) {
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le fichier a été importé avec succès`);
          } else {
            for (const item of Object.entries(res.reports)) {
              let values: any[] = Array.of(item[1]);
              const labelsError: any[] = values.map(val => val.map(v => this.i8nSvc.getLabelFromCode(v.code, v.args.split(','))).join(', '));
              this.errors.push({infosLine: item[0], labelsError});
            }
          }

          for (const item of res.elementsImported) {
            let index = this.taxes.findIndex(atelier => atelier.id == item.id);
            if (index >= 0)
              this.taxes[index] = item;
            else
              this.taxes.push(item)
          }

          if (this.errors?.length)
            this.displayImportErrors();

          $event.target.value = "";
          this.importTooltip.instance.hide();
        });
    }
  }

  calculateSiteValue = (taxe: TaxeDTO): string => taxe?.site?.libelle;

  subscribeTaxSaved = (): void => {
    this.subTaxSaved = this.taxesSvc.taxSaved$
      .subscribe(() => this.fetchData());
  }

}
